import { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { API } from '../../../App';
import { Button } from '@/components/ui/button';
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from '@/components/ui/card';
import { ExternalLink, CheckCircle, XCircle, AlertCircle, RefreshCw, Link as LinkIcon } from 'lucide-react';

const StripeConnectTab = () => {
  const [accountStatus, setAccountStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [hasAccount, setHasAccount] = useState(false);

  useEffect(() => {
    fetchAccountStatus();
  }, []);

  const fetchAccountStatus = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/stripe-connect/account-status`);
      setAccountStatus(response.data);
      setHasAccount(true);
    } catch (error) {
      if (error.response?.status === 404) {
        setHasAccount(false);
      } else {
        toast.error('Failed to fetch Stripe account status');
      }
    } finally {
      setLoading(false);
    }
  };

  const createStripeAccount = async () => {
    setCreating(true);
    try {
      const response = await axios.post(`${API}/stripe-connect/create-account`, {
        country: "US",
        business_type: "individual"
      });
      
      toast.success('Stripe Connect account created! Redirecting to onboarding...');
      
      // Redirect to Stripe onboarding
      window.location.href = response.data.onboarding_url;
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create Stripe account');
      setCreating(false);
    }
  };

  const refreshOnboarding = async () => {
    try {
      const response = await axios.post(`${API}/stripe-connect/refresh-onboarding`);
      toast.info('Redirecting to Stripe onboarding...');
      window.location.href = response.data.onboarding_url;
    } catch (error) {
      toast.error('Failed to generate onboarding link');
    }
  };

  const openStripeDashboard = async () => {
    try {
      const response = await axios.get(`${API}/stripe-connect/dashboard-link`);
      window.open(response.data.url, '_blank');
      toast.success('Opening Stripe Dashboard...');
    } catch (error) {
      toast.error('Failed to open Stripe Dashboard');
    }
  };

  const disconnectAccount = async () => {
    if (!window.confirm('Are you sure you want to disconnect your Stripe account? This will stop all payment processing.')) {
      return;
    }
    
    try {
      await axios.delete(`${API}/stripe-connect/disconnect`);
      toast.success('Stripe account disconnected');
      setHasAccount(false);
      setAccountStatus(null);
    } catch (error) {
      toast.error('Failed to disconnect Stripe account');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-gray-500">Loading...</div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-bold text-gray-900">Stripe Connect</h1>
        <p className="text-gray-600 mt-2">
          Conecte su cuenta de Stripe para recibir pagos de pacientes
        </p>
      </div>

      {/* Explanation Banner */}
      <div className="bg-gradient-to-r from-purple-50 to-indigo-50 border border-purple-200 rounded-lg p-4">
        <div className="flex items-start gap-3">
          <div className="p-2 bg-purple-100 rounded-lg">
            <LinkIcon className="h-5 w-5 text-purple-600" />
          </div>
          <div>
            <h3 className="font-semibold text-purple-900">¿Cuándo usar Stripe Connect?</h3>
            <p className="text-sm text-purple-800 mt-1">
              <strong>Stripe Connect</strong> es ideal si <strong>no tiene una cuenta de Stripe</strong> o prefiere una 
              configuración simplificada. La plataforma gestiona la integración técnica y usted recibe los pagos 
              directamente en su cuenta bancaria después de completar el onboarding.
            </p>
            <p className="text-sm text-purple-700 mt-2">
              💡 <strong>Alternativa:</strong> Si ya tiene una cuenta de Stripe con claves API, puede usar 
              <strong> Settings → Pasarelas de Pago</strong> para una integración directa sin intermediarios.
            </p>
          </div>
        </div>
      </div>

      {!hasAccount ? (
        // No Stripe Account - Setup Card
        <Card>
          <CardHeader>
            <CardTitle>Comenzar con Stripe Connect</CardTitle>
            <CardDescription>
              Conecte su cuenta de Stripe para comenzar a aceptar pagos de pacientes
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <h3 className="font-semibold text-blue-900 mb-2">¿Qué es Stripe Connect?</h3>
              <p className="text-sm text-blue-800">
                Stripe Connect le permite recibir pagos directamente de sus pacientes a través de nuestra plataforma. 
                Nosotros manejamos la integración técnica mientras usted recibe los pagos en su propia cuenta bancaria.
              </p>
            </div>

            <div className="space-y-3">
              <h3 className="font-semibold text-gray-900">Pasos de Configuración:</h3>
              <ol className="list-decimal list-inside space-y-2 text-gray-700">
                <li>Haga clic en "Conectar con Stripe" abajo</li>
                <li>Cree o conecte su cuenta de Stripe</li>
                <li>Complete el proceso de onboarding</li>
                <li>¡Comience a aceptar pagos inmediatamente!</li>
              </ol>
            </div>

            <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
              <h3 className="font-semibold text-amber-900 mb-2">⚡ Comisión de Plataforma</h3>
              <p className="text-sm text-amber-800">
                Se cobra una comisión del 3% en cada transacción para soportar la plataforma y los servicios.
              </p>
            </div>

            <Button
              onClick={createStripeAccount}
              disabled={creating}
              className="w-full bg-teal-600 hover:bg-teal-700"
              size="lg"
            >
              {creating ? (
                <>
                  <RefreshCw className="h-5 w-5 mr-2 animate-spin" />
                  Creando Cuenta...
                </>
              ) : (
                <>
                  <LinkIcon className="h-5 w-5 mr-2" />
                  Conectar con Stripe
                </>
              )}
            </Button>
          </CardContent>
        </Card>
      ) : (
        // Has Stripe Account - Status Cards
        <>
          {/* Account Status Card */}
          <Card>
            <CardHeader>
              <CardTitle>Stripe Account Status</CardTitle>
              <CardDescription>
                Monitor your Stripe Connect account status and capabilities
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="flex items-center gap-3 p-4 bg-gray-50 rounded-lg">
                  {accountStatus?.details_submitted ? (
                    <CheckCircle className="h-6 w-6 text-green-600" />
                  ) : (
                    <AlertCircle className="h-6 w-6 text-amber-600" />
                  )}
                  <div>
                    <p className="font-medium text-gray-900">Onboarding</p>
                    <p className="text-sm text-gray-600">
                      {accountStatus?.details_submitted ? 'Complete' : 'Incomplete'}
                    </p>
                  </div>
                </div>

                <div className="flex items-center gap-3 p-4 bg-gray-50 rounded-lg">
                  {accountStatus?.charges_enabled ? (
                    <CheckCircle className="h-6 w-6 text-green-600" />
                  ) : (
                    <XCircle className="h-6 w-6 text-red-600" />
                  )}
                  <div>
                    <p className="font-medium text-gray-900">Charges</p>
                    <p className="text-sm text-gray-600">
                      {accountStatus?.charges_enabled ? 'Enabled' : 'Disabled'}
                    </p>
                  </div>
                </div>

                <div className="flex items-center gap-3 p-4 bg-gray-50 rounded-lg">
                  {accountStatus?.payouts_enabled ? (
                    <CheckCircle className="h-6 w-6 text-green-600" />
                  ) : (
                    <XCircle className="h-6 w-6 text-red-600" />
                  )}
                  <div>
                    <p className="font-medium text-gray-900">Payouts</p>
                    <p className="text-sm text-gray-600">
                      {accountStatus?.payouts_enabled ? 'Enabled' : 'Disabled'}
                    </p>
                  </div>
                </div>

                <div className="flex items-center gap-3 p-4 bg-gray-50 rounded-lg">
                  <div className="h-6 w-6 bg-teal-100 rounded-full flex items-center justify-center">
                    <span className="text-teal-600 font-semibold text-sm">ID</span>
                  </div>
                  <div>
                    <p className="font-medium text-gray-900">Account ID</p>
                    <p className="text-sm text-gray-600 font-mono">
                      {accountStatus?.account_id?.slice(0, 20)}...
                    </p>
                  </div>
                </div>
              </div>

              {!accountStatus?.details_submitted && (
                <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
                  <p className="text-sm text-amber-800">
                    ⚠️ Your onboarding is incomplete. Complete it to start accepting payments.
                  </p>
                </div>
              )}

              {accountStatus?.requirements && Object.keys(accountStatus.requirements).length > 0 && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                  <p className="text-sm text-red-800 font-semibold">
                    Additional Information Required
                  </p>
                  <p className="text-sm text-red-700 mt-1">
                    Stripe requires additional verification to keep your account active.
                  </p>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Actions Card */}
          <Card>
            <CardHeader>
              <CardTitle>Account Actions</CardTitle>
              <CardDescription>
                Manage your Stripe Connect integration
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-3">
              <Button
                onClick={openStripeDashboard}
                variant="outline"
                className="w-full"
              >
                <ExternalLink className="h-4 w-4 mr-2" />
                Open Stripe Dashboard
              </Button>

              {!accountStatus?.details_submitted && (
                <Button
                  onClick={refreshOnboarding}
                  className="w-full bg-teal-600 hover:bg-teal-700"
                >
                  <RefreshCw className="h-4 w-4 mr-2" />
                  Complete Onboarding
                </Button>
              )}

              <Button
                onClick={fetchAccountStatus}
                variant="outline"
                className="w-full"
              >
                <RefreshCw className="h-4 w-4 mr-2" />
                Refresh Status
              </Button>

              <Button
                onClick={disconnectAccount}
                variant="destructive"
                className="w-full"
              >
                Disconnect Stripe Account
              </Button>
            </CardContent>
          </Card>

          {/* Information Card */}
          <Card>
            <CardHeader>
              <CardTitle>Payment Information</CardTitle>
              <CardDescription>
                How payments work with Stripe Connect
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <div>
                  <h3 className="font-semibold text-gray-900 mb-2">Payment Flow</h3>
                  <ol className="list-decimal list-inside space-y-2 text-gray-700 text-sm">
                    <li>Patient pays for membership through your website or WordPress</li>
                    <li>Payment is processed through Stripe</li>
                    <li>Platform fee (3%) is automatically deducted</li>
                    <li>Remaining amount is transferred to your Stripe account</li>
                    <li>You receive payouts based on your Stripe payout schedule</li>
                  </ol>
                </div>

                <div>
                  <h3 className="font-semibold text-gray-900 mb-2">Payout Schedule</h3>
                  <p className="text-sm text-gray-700">
                    Payouts are typically sent to your bank account within 2-7 business days. 
                    You can configure your payout schedule in the Stripe Dashboard.
                  </p>
                </div>

                <div>
                  <h3 className="font-semibold text-gray-900 mb-2">Support</h3>
                  <p className="text-sm text-gray-700">
                    For questions about payouts, disputes, or Stripe-specific issues, 
                    visit the Stripe Dashboard or contact Stripe support directly.
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        </>
      )}
    </div>
  );
};

export default StripeConnectTab;
